import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SectionLabel, TiltCard } from '../components/Effects';
import { ArrowIcon } from '../components/Icons';
import { HALLOWEEN_EVENT_DETAILS, type EventGalleryImage } from '../data/events';

export function HalloweenEventPage() {
  const event = HALLOWEEN_EVENT_DETAILS;
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const [copiedTp, setCopiedTp] = useState(false);

  // Lightbox keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedPhotoIndex === null) return;
      if (e.key === 'Escape') {
        setSelectedPhotoIndex(null);
      } else if (e.key === 'ArrowRight') {
        setSelectedPhotoIndex((prev) => (prev !== null ? (prev + 1) % event.gallery.length : 0));
      } else if (e.key === 'ArrowLeft') {
        setSelectedPhotoIndex((prev) => (prev !== null ? (prev - 1 + event.gallery.length) % event.gallery.length : 0));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPhotoIndex, event.gallery.length]);

  const handleCopyTp = () => {
    navigator.clipboard.writeText('/tp 7000 69 6500');
    setCopiedTp(true);
    setTimeout(() => setCopiedTp(false), 2500);
  };

  const selectedPhoto: EventGalleryImage | null =
    selectedPhotoIndex !== null ? event.gallery[selectedPhotoIndex] : null;

  return (
    <div className="halloween-memorial-page">
      {/* Native Web Master Page Hero */}
      <section className="page-hero halloween-page-hero">
        <div
          className="page-hero-bg"
          style={{
            backgroundImage: `linear-gradient(90deg, rgba(3, 9, 8, 0.94) 0%, rgba(4, 10, 9, 0.65) 50%, rgba(3, 8, 7, 0.85) 100%), url('${event.heroImage}')`,
            backgroundPosition: 'center',
            backgroundSize: 'cover'
          }}
        />

        <div className="page-hero-copy">
          <div className="eyebrow">
            <span /> OFFICIAL ARCHIVE • HALLOWEEN 2026
          </div>
          <h1>
            HAUNTED OCEAN<br />
            <em>REALM CITADEL.</em>
          </h1>
          <p>
            The monumental Halloween 2026 celebration concluded across the abyssal ocean (X: 7000, Z: 6500) with 10 master-builder plots—shadowed in its final hour by a sudden extraterrestrial strike.
          </p>

          <div className="page-hero-actions">
            <a className="outline-link" href="#chronicle-overview">
              Read Chronicle <ArrowIcon />
            </a>
            <a className="outline-link" href="#gallery-showcase">
              View 4K Gallery ({event.gallery.length}) <ArrowIcon />
            </a>
            <Link className="ghost-link" to="/events">
              ← Back to Event Roadmap
            </Link>
          </div>
        </div>

        {/* Sovereign Seal Element */}
        <div className="constitution-seal event-seal">
          <img src="/assets/ugeappage-logo-zoomed.webp" alt="Halloween 2026 Seal" />
          <span>EST. 2026</span>
          <b>HALLOWEEN</b>
          <small>OCEAN CITADEL</small>
        </div>
      </section>

      {/* Key Telemetry Ribbon Strip */}
      <section className="telemetry-bar-strip">
        <div className="telemetry-inner-container">
          {event.stats.map((stat) => (
            <div key={stat.label} className="telemetry-metric-tile">
              <span className="telemetry-tile-icon">{stat.icon}</span>
              <div>
                <b>{stat.value}</b>
                <small>{stat.label}</small>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Chronicle & Lore Section */}
      <section id="chronicle-overview" className="section-pad">
        <SectionLabel index="ARCHIVE">THE CHRONICLE</SectionLabel>

        <div className="split-heading" data-reveal>
          <h2>
            The Maritime Colosseum<br />
            <em>& The Sudden Incursion</em>
          </h2>
          <p>
            A triumphant Halloween festival celebrating 10 sovereign creators and deep ocean engineering—interrupted by an unexpected alien strike from beyond the stars.
          </p>
        </div>

        {/* Narrative & Ingress Grid */}
        <div className="chronicle-content-grid">
          <div className="chronicle-prose-card" data-reveal>
            <div className="chronicle-header-badge">
              <span>HISTORICAL RECORD • OCTOBER 2026</span>
              <span className="live-status-pill">✓ PERMANENT SANCTUARY</span>
            </div>

            <div className="chronicle-body">
              {event.overview.map((para, i) => (
                <p key={i} className="chronicle-text">{para}</p>
              ))}
            </div>

            {/* In-Game Ingress Callout */}
            <div className="in-game-teleport-box">
              <div className="teleport-box-header">
                <span className="suit-icon">🎩</span>
                <div>
                  <h4>Permanent In-Game Portal</h4>
                  <p>Speak to the suit-clad <b>Event Manager</b> at the Grand Hub spawn, or use the direct command:</p>
                </div>
              </div>
              <button
                type="button"
                className={`teleport-command-btn ${copiedTp ? 'copied' : ''}`}
                onClick={handleCopyTp}
              >
                <span>{copiedTp ? '✓ Copied to Clipboard!' : '/tp 7000 69 6500'}</span>
              </button>
            </div>
          </div>

          {/* Architectural Lore Cards Stack */}
          <div className="chronicle-lore-stack">
            {event.lore.map((item, idx) => (
              <TiltCard key={idx} className="lore-tilt-card">
                <div className="lore-card-inner">
                  <div className="lore-card-head">
                    <span className="lore-ico">{item.icon}</span>
                    <h3>{item.heading}</h3>
                  </div>
                  <p>{item.text}</p>
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {/* Cataclysmic Incursion Dramatic Banner */}
      <section className="aether-cataclysm-strip">
        <div className="aether-strip-inner">
          <div className="aether-pulse-badge">☄️</div>
          <div className="aether-strip-text">
            <h3>The Ominous Celestial Incursion</h3>
            <p>A triumphant celebration cut short by uninvited extraterrestrial forces • Blood-red skies, sonic shockwaves, and an alien impact crater hinting at dark perils yet to come.</p>
          </div>
          <div className="aether-clean-tag">
            <span>CLASSIFIED FOREWARNING</span>
          </div>
        </div>
      </section>

      {/* 4K Cinematic Gallery Section */}
      <section id="gallery-showcase" className="section-pad gallery-showcase-section">
        <SectionLabel index="GALLERY">4K MEMORY VAULT</SectionLabel>

        <div className="split-heading gallery-split-heading" data-reveal>
          <div>
            <h2>
              Cinematic Captures<br />
              <em>& High-Fidelity Records</em>
            </h2>
            <p>
              13 Ultra-HD captures immortalizing the maritime colosseum, builder plots, and the alien meteor core. Click any photo to inspect in full 4K.
            </p>
          </div>
        </div>

        {/* Gallery Visual Grid */}
        <div className="gallery-card-deck">
          {event.gallery.map((photo, originalIndex) => (
            <div
              key={photo.src}
              className="gallery-deck-item"
              onClick={() => setSelectedPhotoIndex(originalIndex)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setSelectedPhotoIndex(originalIndex);
                }
              }}
            >
                <div className="deck-item-media">
                  <img
                    src={photo.thumb || photo.src}
                    alt={photo.title}
                    className="deck-item-img"
                    loading="lazy"
                    decoding="async"
                    width="960"
                    height="540"
                  />
                  <div className="deck-item-gradient" />

                  <div className="deck-top-ribbon">
                    <span className="deck-num">#{String(originalIndex + 1).padStart(2, '0')}</span>
                    <span className="deck-tag-4k">4K UHD</span>
                  </div>

                  <div className="deck-hover-overlay">
                    <span className="deck-hover-icon">🔍</span>
                  </div>
                </div>
              </div>
          ))}
        </div>
      </section>

      {/* Advanced Fullscreen Lightbox Modal */}
      {selectedPhoto !== null && selectedPhotoIndex !== null && (
        <div
          className="deck-lightbox-overlay"
          onClick={() => setSelectedPhotoIndex(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="deck-lightbox-frame" onClick={(e) => e.stopPropagation()}>
            {/* Header Toolbar */}
            <div className="lightbox-frame-head">
              <span className="frame-counter">
                Photo {selectedPhotoIndex + 1} of {event.gallery.length}
              </span>
              <div className="frame-actions">
                <span className="frame-res-badge">4K Ultra-HD Master</span>
                <button
                  type="button"
                  className="frame-close-btn"
                  onClick={() => setSelectedPhotoIndex(null)}
                  aria-label="Close Lightbox (Esc)"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Viewport */}
            <div className="lightbox-frame-viewport">
              <img
                src={selectedPhoto.src}
                alt={selectedPhoto.title}
                className="lightbox-master-img"
              />

              <button
                type="button"
                className="lightbox-stage-arrow prev-arrow"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedPhotoIndex(
                    (selectedPhotoIndex - 1 + event.gallery.length) % event.gallery.length
                  );
                }}
                aria-label="Previous (Left arrow)"
              >
                ‹
              </button>
              <button
                type="button"
                className="lightbox-stage-arrow next-arrow"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedPhotoIndex(
                    (selectedPhotoIndex + 1) % event.gallery.length
                  );
                }}
                aria-label="Next (Right arrow)"
              >
                ›
              </button>
            </div>

            {/* Footer Minimal Bar */}
            <div className="lightbox-frame-footer">
              <span className="lightbox-counter-tag">
                PHOTO #{String(selectedPhotoIndex + 1).padStart(2, '0')} OF {String(event.gallery.length).padStart(2, '0')}
              </span>
              {selectedPhoto.photographer && (
                <div className="lightbox-author-tag">
                  Captured by <b>{selectedPhoto.photographer}</b>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Page Return Navigation */}
      <div className="page-bottom-return-bar">
        <Link to="/events" className="outline-link">
          ← Return to Sovereign Event Roadmap
        </Link>
        <a href="#gallery-showcase" className="ghost-link">
          ↑ Back to Top of Gallery
        </a>
      </div>
    </div>
  );
}
